import { useEffect } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import Coordinates, { CoordinateData } from "../../models/Coordinates";
import { BaseField } from "../../models/extraction/BaseField";
import { ExtractedFieldData } from "../../models/extraction/ExtractedFieldData";
import { HighlightObjectFactory } from "../../models/HighlightObjectFactory";
import { PageSpans } from "../../models/Page";
import Span from "../../models/Span";
import { useHighlightDispatch } from "../highlightProvider/HighlightProvider";

type Mock = { pageId: string; coords: Coordinates[] };
function mockSpansFromBaseFelds(fields: Array<BaseField | undefined>): Mock[] {
  fields = fields.filter((x) => x != undefined);
  const pageSpans: Mock[] = fields
    .map((x) => x!.pageId)
    .filter((x, i, a) => a.indexOf(x) === i)
    .map((x) => ({ pageId: x, coords: [] }));

  const coords = fields.map((x) => {
    return new Coordinates({
      pageId: x?.pageId,
      x0: x?.topLeft?.x,
      y0: x?.topLeft?.y,
      x1: x?.bottomRight?.x,
      y1: x?.bottomRight?.y,
    });
  });
  pageSpans.forEach((pageSpan) => {
    pageSpan.coords = coords.filter((x) => x.pageId === pageSpan.pageId);
  });
  return pageSpans;
}

// TODO: highlight weird spots
// TODO: highlight spans, duplicate ids
// DONE: filter by page index in ImageViewer
function useSpanHighlights(
  highlightKey: string,
  color: string,
  acceleratorDoc: AcceleratorDoc,
  extractedFieldData: ExtractedFieldData,
  sendExtraction: boolean,
  sendReuse: boolean,
) {
  const highlightDispatch = useHighlightDispatch();

  useEffect(() => {
    let mounted = true;

    //console.debug("Spans for AcceleratorDoc loaded");
    const extraction = extractedFieldData.getAtIndex();
    const reuse = extractedFieldData.getReuse() ?? [];
    const pageIds = [extraction?.pageId]
      .concat(reuse.map((x) => x?.pageId))
      .filter((x) => x != undefined) as string[];
    const reuseSpans = reuse.flatMap((r) => r?.spanIds ?? []) ?? [];

    if (!sendExtraction && !sendReuse) {
      highlightDispatch({
        type: "remove",
        highlight: [
          HighlightObjectFactory.createExtractHighlight(
            highlightKey,
            color,
            Span.createTest(),
            false,
          ),
        ],
      });
      return;
    }

    if (!extraction && reuse.length === 0) {
      console.debug("Not rendering for blank document");
      return;
    }

    acceleratorDoc.getAllPageSpans().then((pageSpans) => {
      const spans = pageSpans.flatMap((x) => x.spans);
      // Promise.all(pageIds.map(x => acceleratorDoc.getPageSpansById(x))).then(pageSpans => {
      //     const spans = pageSpans.flatMap(x => x.spans);

      const higlights = reuseSpans
        .map((spanId) => spans.find((x) => x.id === spanId))
        .filter((x) => x != undefined)
        .filter((x, i, a) => a.indexOf(x) === i)
        .map((span) =>
          HighlightObjectFactory.createReuseHighlight(
            highlightKey,
            color,
            span!,
            sendReuse,
          ),
        );

      // const extractedHighlights = extraction ? : undefined;
      if (extraction) {
        extraction.spanIds //.filter((x, i, a) => a.indexOf(x) === i)
          .forEach((spanId) => {
            higlights.push(
              HighlightObjectFactory.createExtractHighlight(
                highlightKey,
                color,
                spans.find((x) => x.id === spanId)!,
                sendExtraction,
              ),
            );
          });
        // higlights.push(HighlightObjectFactory.createExtractHighlight(
        //     highlightKey,
        //     color,
        //     ,
        //     sendExtraction
        // ))
        // higlights = higlights.concat(extraction.spanIds.map(span => HighlightObjectFactory.createExtractHighlight(
        //     highlightKey,
        //     color,
        //     spans.find(s => s.id === span)!,
        //     sendExtraction
        // )))
      }

      if (mounted) {
        highlightDispatch({
          type: "add",
          highlight: higlights,
        });
      }
    });

    return function () {
      //console.debug("Clearing Span highlights");
      mounted = false;
      highlightDispatch({
        type: "remove",
        highlight: [
          HighlightObjectFactory.createExtractHighlight(
            highlightKey,
            color,
            Span.createTest(),
            false,
          ),
        ],
      });
    };
  }, [
    highlightKey,
    color,
    sendReuse,
    sendExtraction,
    extractedFieldData.extraction,
  ]);
}

export { useSpanHighlights };
